import { useZones } from 'features/Responder/features/Zones';
import 'features/Responder/features/Schedules/features/RunEventHistory/eventHistory.css';
import { useZones } from 'features/Responder/features/Zones';
import React, { useCallback, useMemo } from 'react';
import { Card, Stack } from 'react-bootstrap';
import { useTours } from 'features/Responder/features/Tours/contexts/TourContext';
import checkpointDomId from 'features/Responder/features/Tours/features/TourBuilder/utils/checkpointDomId';
import stopLabelFromIndex from 'features/Responder/features/Tours/features/TourBuilder/utils/stopLabelFormIndex';
import {
  formatAddress,
  formatCoords,
  formatLocationLabel,
  modeLabel,
  shapeLabel,
} from './utils/checkpointListFormatters';

/**
 * Lists checkpoints with name, shape, mode, and address so builders can quickly jump to edit.
 * Clicking a row scrolls the tour builder to the corresponding checkpoint card.
 * @returns {JSX.Element}
 */
export default function CheckpointList({ maxHeight = null }) {
  const {
    stops = [],
    setSelectedCheckpointId,
    selectedCheckpointId,
    activeTourData,
    builderTourData,
    startLocation,
    setCenter,
  } = useTours();
  const { zones: zonesRaw } = useZones();
  const zones = useMemo(() => (Array.isArray(zonesRaw) ? zonesRaw : []), [zonesRaw]);

  const checkpointRows = useMemo(() => {
    return (stops || []).map((stop, idx) => {
      const displayName =
        typeof stop?.label === 'string' && stop.label.trim().length
          ? stop.label.trim()
          : stopLabelFromIndex(idx);
      return {
        id: stop?.id ?? idx,
        stop,
        idx,
        displayName,
        shape: shapeLabel(stop),
        mode: modeLabel(stop?.mode),
        address: formatAddress(stop),
      };
    });
  }, [stops]);

  const startInfo = useMemo(() => {
    const node0 = activeTourData?.nodes?.[0];
    const address = formatLocationLabel(
      {
        ...node0,
        ...startLocation,
        zoneId: startLocation?.zoneId ?? node0?.zoneId ?? node0?.geometry?.zoneId,
      },
      zones,
    );
    const lat = Number(
      startLocation?.lat ?? node0?.coords?.[0] ?? node0?.coordinates?.[0] ?? node0?.location?.lat,
    );
    const lng = Number(
      startLocation?.lng ?? node0?.coords?.[1] ?? node0?.coordinates?.[1] ?? node0?.location?.lng,
    );
    const coords =
      Number.isFinite(lat) && Number.isFinite(lng) ? `${lat.toFixed(5)}, ${lng.toFixed(5)}` : 'N/A';
    return { address: address || 'N/A', coords, lat, lng };
  }, [activeTourData?.nodes, startLocation, zones]);

  // Use TourBuilder logic for looping detection
  const isLoopingTour = useMemo(() => {
    if (typeof builderTourData?.isLooped === 'boolean') {
      return builderTourData.isLooped;
    }
    // Defensive: nodes array must exist and have at least 2 elements
    const nodes = activeTourData?.nodes;
    if (!Array.isArray(nodes) || nodes.length < 2) return false;
    // Compare first and last node id
    const firstId = nodes[0]?.id;
    const lastId = nodes[nodes.length - 1]?.id;
    return firstId && lastId && firstId === lastId;
  }, [activeTourData, builderTourData?.isLooped]);

  const destinationInfo = useMemo(() => {
    if (isLoopingTour) {
      return {
        address: startInfo.address,
        coords: startInfo.coords,
        lat: startInfo.lat,
        lng: startInfo.lng,
        stop: null,
      };
    }
    const lastStop = stops?.length ? stops[stops.length - 1] : null;
    if (!lastStop) {
      return {
        address: 'N/A',
        coords: 'N/A',
        lat: null,
        lng: null,
        stop: null,
      };
    }
    return {
      address: formatLocationLabel(lastStop, zones),
      coords: formatCoords(lastStop),
      lat: Number.parseFloat(lastStop.lat ?? lastStop?.coords?.[0] ?? lastStop?.coordinates?.[0]),
      lng: Number.parseFloat(lastStop.lng ?? lastStop?.coords?.[1] ?? lastStop?.coordinates?.[1]),
      stop: lastStop,
    };
  }, [isLoopingTour, startInfo.address, startInfo.coords, stops, zones]);

  const focusMapAt = (lat, lng) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    setCenter?.([lat, lng, 16]);
  };

  const scrollToStart = () => {
    if (typeof setSelectedCheckpointId === 'function') {
      setSelectedCheckpointId('start');
    }
    if (typeof document !== 'undefined') {
      const el = document.getElementById('checkpoint-editor-start');
      if (el?.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const focusable = el.querySelector('input, textarea, select, button, [tabindex]');
        focusable?.focus({ preventScroll: true });
      }
    }
    focusMapAt(startInfo.lat, startInfo.lng);
  };

  const scrollToDestination = () => {
    if (isLoopingTour || !destinationInfo.stop) {
      scrollToStart();
      return;
    }
    const idx = stops.length - 1;
    if (idx < 0) return;
    const row = checkpointRows[idx];
    if (row) {
      handleScrollToCheckpoint(row);
    }
    focusMapAt(destinationInfo.lat, destinationInfo.lng);
  };

  const handleScrollToCheckpoint = useCallback(
    (row) => {
      if (!row) return;
      if (typeof setSelectedCheckpointId === 'function') {
        setSelectedCheckpointId(`stop-${row.stop?.id ?? row.idx}`);
      }
      if (typeof document === 'undefined') return;
      const targetId = checkpointDomId(row.stop, row.idx);
      const el = document.getElementById(targetId);
      if (el?.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const focusable = el.querySelector('input, textarea, select, button, [tabindex]');
        focusable?.focus({ preventScroll: true });
      }
    },
    [setSelectedCheckpointId],
  );

  const cardStyle = maxHeight ? { height: '100%', maxHeight } : { height: '100%' };

  const shouldRenderList = Boolean(
    startLocation?.address || startLocation?.lat || startLocation?.lng,
  );
  const hasValidTour = shouldRenderList && stops.length > 0;
  const listContent = shouldRenderList ? (
    <Stack gap={2}>
      {/* Render start location from context */}
      <Card
        className={`shadow-sm event-history-item${
          selectedCheckpointId === 'start' ? ' is-selected' : ''
        }`}
        role="button"
        tabIndex={0}
        onClick={scrollToStart}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            scrollToStart();
          }
        }}
        style={{ cursor: 'pointer' }}
      >
        <Card.Body className="p-3 d-flex flex-column gap-1">
          <div className="fw-semibold">Start Location</div>
          <div className="text-muted small">{startInfo.address}</div>
          <div className="text-muted small">{startInfo.coords}</div>
        </Card.Body>
      </Card>
      {checkpointRows.map((row) => (
        <Card
          key={row.id}
          className={`shadow-sm event-history-item${
            selectedCheckpointId === `stop-${row.stop?.id ?? row.idx}` ? ' is-selected' : ''
          }`}
          role="button"
          tabIndex={0}
          onClick={() => handleScrollToCheckpoint(row)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleScrollToCheckpoint(row);
            }
          }}
          style={{ cursor: 'pointer' }}
        >
          <Card.Body className="p-3 d-flex flex-column gap-1">
            <div className="fw-semibold">{row.displayName}</div>
            <div className="text-muted small">Shape: {row.shape}</div>
            <div className="text-muted small">Mode: {row.mode}</div>
            <div className="text-muted small">Address: {row.address}</div>
          </Card.Body>
        </Card>
      ))}
      {hasValidTour && (
        <Card
          className={`shadow-sm event-history-item${
            selectedCheckpointId === 'destination' ? ' is-selected' : ''
          }`}
          role="button"
          tabIndex={0}
          onClick={scrollToDestination}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              scrollToDestination();
            }
          }}
          style={{ cursor: 'pointer' }}
        >
          <Card.Body className="p-3 d-flex flex-column gap-1">
            <div className="fw-semibold">Destination</div>
            <div className="text-muted small">{destinationInfo.address}</div>
            <div className="text-muted small">{destinationInfo.coords}</div>
          </Card.Body>
        </Card>
      )}
      {!checkpointRows.length && (
        <div className="text-muted small">
          Add checkpoints in the Tour Builder to see them listed here.
        </div>
      )}
    </Stack>
  ) : (
    <div className="text-muted small">Select a start location to see checkpoints here.</div>
  );

  return (
    <Card className="flex-grow-1 w-100 h-100 event-history-panel" style={cardStyle}>
      <Card.Body className="d-flex flex-column gap-3 h-100" style={{ minHeight: 0 }}>
        <div>
          <Card.Title>Checkpoint List</Card.Title>
          <Card.Text className="text-muted small mb-0">
            Click a checkpoint to jump to its editor in the Tour Builder.
          </Card.Text>
        </div>

        <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
          <div className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
            {listContent}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
